import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Link2, Users } from "lucide-react";
import { Btn, inputCls, Modal, Card } from "@/components/cc";
import { TYPE_LABEL, STATUS_COLOR } from "@/pages/command/entity/EntityProfiles";

// Reusable reciprocal entity-link control (Phase 2F). Reads/writes the central entity_links
// join via /entities/for-record and /entities/link-record. Two variants: "card" (default) and
// "inline" (compact button, for timeline events).
export function EntityLinkManager({ linkType, recordId, canManage = true, note, variant = "card" }) {
  const [links, setLinks] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [modal, setModal] = useState(false);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [loadingOpts, setLoadingOpts] = useState(false);

  const load = useCallback(() => {
    if (!recordId) return;
    api.get("/entities/for-record", { params: { link_type: linkType, record_id: recordId } })
      .then((r) => setLinks(r.data)).catch(() => {});
  }, [linkType, recordId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/entities/meta").then((r) => setEntityTypes(r.data.entity_types || [])).catch(() => {}); }, []);

  const fetchOptions = useCallback(() => {
    setLoadingOpts(true);
    const params = {};
    if (q) params.q = q;
    if (typeFilter) params.entity_type = typeFilter;
    api.get("/entities/entities", { params }).then((r) => setOptions(r.data)).catch(() => {}).finally(() => setLoadingOpts(false));
  }, [q, typeFilter]);

  const openManage = () => { setSelected(links.map((l) => l.id)); setQ(""); setTypeFilter(""); setOptions([]); setModal(true); };
  useEffect(() => { if (!modal) return; const t = setTimeout(fetchOptions, 250); return () => clearTimeout(t); }, [modal, fetchOptions]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const save = async () => {
    try {
      await api.post("/entities/link-record", { link_type: linkType, record_id: recordId, entity_ids: selected });
      toast.success("Entity links updated"); setModal(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const modalEl = (
    <Modal open={modal} onClose={() => setModal(false)} title="Link Entities" wide>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <input data-testid="entlink-search" className={`${inputCls} col-span-2`} placeholder="Search canonical / display name or alias" value={q} onChange={(e) => setQ(e.target.value)} />
          <select data-testid="entlink-type-filter" className={inputCls} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">All types</option>{entityTypes.map((t) => <option key={t} value={t}>{TYPE_LABEL[t] || t}</option>)}
          </select>
        </div>
        <div className="max-h-80 overflow-y-auto space-y-1" data-testid="entlink-options">
          {loadingOpts && <p className="text-xs text-zinc-600">Searching…</p>}
          {!loadingOpts && options.length === 0 && <p className="text-xs text-zinc-600">No active entities found. Create them in Entity Profiles first.</p>}
          {!loadingOpts && options.map((o) => (
            <label key={o.id} className="flex items-center gap-2 text-sm border border-zinc-800 px-3 py-2 rounded-sm cursor-pointer hover:border-zinc-600">
              <input type="checkbox" data-testid={`entlink-opt-${o.id}`} checked={selected.includes(o.id)} onChange={() => toggle(o.id)} />
              <span className="truncate">{o.display_name}</span>
              <span className="text-[0.6rem] px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-400 ml-auto shrink-0">{TYPE_LABEL[o.entity_type] || o.entity_type}</span>
              {o.country && <span className="text-[0.6rem] text-zinc-600 shrink-0">{o.country}</span>}
              {o.status && <span className={`text-[0.6rem] uppercase shrink-0 ${STATUS_COLOR[o.status] || ""}`}>{o.status}</span>}
            </label>
          ))}
        </div>
        <p className="text-[0.7rem] text-zinc-600">Only active entities are offered as new targets; existing links to archived entities are preserved. Linking is an editorial association only — it implies no guilt, endorsement, authorship, responsibility or verification.</p>
        <Btn data-testid="entlink-save" onClick={save} className="w-full justify-center">Save Links</Btn>
      </div>
    </Modal>
  );

  if (variant === "inline") {
    return (
      <>
        <button type="button" data-testid={`entlink-inline-${recordId}`} disabled={!canManage} onClick={openManage}
          className={`inline-flex items-center gap-1 text-[0.65rem] px-1.5 py-0.5 rounded-sm border transition-colors disabled:opacity-40 ${links.length > 0 ? "border-violet-500/40 bg-violet-500/10 text-violet-300" : "border-zinc-800 text-zinc-500 hover:border-zinc-600"}`}>
          <Users className="h-3 w-3" /> Entities {links.length > 0 && `(${links.length})`}
        </button>
        {modalEl}
      </>
    );
  }

  return (
    <Card className="p-4" data-testid={`entlink-card-${linkType}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-tactical font-bold text-sm">Linked Entities</h3>
        {canManage && <Btn variant="ghost" data-testid={`entlink-manage-${linkType}`} onClick={openManage}><Link2 className="h-4 w-4" /> Manage</Btn>}
      </div>
      {note && <p className="text-[0.7rem] text-zinc-600 mb-2">{note}</p>}
      {links.length === 0 ? <p className="text-xs text-zinc-600">No entities linked.</p> : (
        <ul className="space-y-1 text-sm" data-testid={`entlink-list-${linkType}`}>
          {links.map((e) => (
            <li key={e.id} className="text-zinc-300 flex flex-wrap items-center gap-2" data-testid={`entlink-item-${e.id}`}>
              • <span>{e.display_name}</span>
              <span className="text-[0.6rem] px-1.5 py-0.5 rounded-sm border border-zinc-700 text-zinc-400">{TYPE_LABEL[e.entity_type] || e.entity_type}</span>
              {e.country && <span className="text-xs text-zinc-600">{e.country}</span>}
              {e.archived && <span className="text-[0.6rem] text-amber-400 uppercase">archived</span>}
            </li>
          ))}
        </ul>
      )}
      {modalEl}
    </Card>
  );
}
